import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Hash, MoreVertical, Plus, Smile, AtSign, Send, Type } from 'lucide-react';
// Real people's headshots so the Slack conversation feels like actual
// teammates, not illustrated avatars.
const TEAM_AVATARS = {
  marcus: 'https://cdn.magicpatterns.com/uploads/cQfPG6P6A11v9JzXpJGr7j/ChatGPT_Image_Jun_10,_2026,_11_38_48_PM.png',
  elena: 'https://cdn.magicpatterns.com/uploads/fDuyDQ8s8Kj3vHuNijXXza/CleanShot_2026-06-19_at_10.54.362x.png',
  priya: 'https://cdn.magicpatterns.com/uploads/nAAhN9ADKm7mmT6DcTrW1d/3d8931bea09198600764c69117c93eda.jpg'
};
// Authentic four-color Slack mark, inlined as SVG so it never falls back to a
// flat monochrome CDN rendering.
function SlackLogo({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 122.8 122.8" className={className} aria-hidden="true" data-id="element-2455">
      <path d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z" fill="#E01E5A" data-id="element-2456" />
      <path d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z" fill="#36C5F0" data-id="element-2457" />
      <path d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z" fill="#2EB67D" data-id="element-2458" />
      <path d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z" fill="#ECB22E" data-id="element-2459" />
    </svg>;
}
const SLUGS = ['stripe', 'github', 'figma', 'salesforce', 'hubspot', 'notion', 'linear', 'slack', 'google', 'zoom', 'jira', 'asana', 'zendesk', 'intercom', 'snowflake', 'datadog', 'sentry', 'aws', 'googlecloud', 'microsoftazure', 'vercel', 'netlify', 'docker', 'kubernetes', 'postgresql', 'mysql', 'redis', 'mongodb', 'react', 'typescript', 'python', 'go', 'rust', 'java', 'cplusplus', 'csharp', 'swift', 'kotlin', 'dart', 'flutter', 'adobephotoshop', 'adobeillustrator', 'adobeindesign', 'adobepremierepro', 'adobeaftereffects', 'adobelightroom', 'adobexd', 'sketch', 'invision', 'framer', 'webflow', 'wix', 'squarespace', 'wordpress', 'shopify', 'magento', 'woocommerce', 'bigcommerce', 'contentful', 'strapi', 'sanity', 'supabase', 'firebase', 'auth0', 'okta', 'clerk', 'rippling', 'gusto', 'workday', 'adp'];
// Three Giga result lines — concise, metric-rich, scannable.
const GIGA_METRICS = [{
  label: 'Pipeline this month',
  value: '$2.4M',
  delta: '+18% MoM'
}, {
  label: 'Deals slipping',
  value: '7 deals',
  delta: '$310K at risk'
}, {
  label: 'Actions taken',
  value: 'Paused 4 stale sequences, pinged owners'
}];
// Self-contained, fast conversation that loops: teammate asks a metric-driven
// question, Giga thinks for a beat, then answers with concrete numbers.
function SlackPanel() {
  // phase: 0 = reading, 1 = Giga thinking, 2 = Giga metric answer
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const timers: number[] = [];
    const run = () => {
      setPhase(0);
      timers.push(window.setTimeout(() => setPhase(1), 900));
      timers.push(window.setTimeout(() => setPhase(2), 1900));
    };
    run();
    const loop = window.setInterval(run, 5200);
    return () => {
      timers.forEach(clearTimeout);
      clearInterval(loop);
    };
  }, []);
  return <div className="relative z-30 w-full max-w-[420px] shrink-0" data-id="element-2460">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1]
    }} className="bg-[#1A1D21] rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col" data-id="element-2461">
        {/* Workspace bar — the small aubergine strip at the top of Slack */}
        <div className="flex items-center gap-2 bg-[#3F0E40] px-4 py-2" data-id="element-2462">
          <SlackLogo className="w-4 h-4" data-id="element-2463" />
          <span className="text-white/90 text-[13px] font-bold" data-id="element-2464">Giga HQ</span>
        </div>

        {/* Channel header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]" data-id="element-2465">
          <div className="flex items-center gap-1.5" data-id="element-2466">
            <Hash size={15} className="text-white/55" data-id="element-2467" />
            <span className="text-white font-bold text-[15px]" data-id="element-2468">
              revenue-ops
            </span>
            <span className="text-white/35 text-xs ml-1" data-id="element-2469">312</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-2470">
            <div className="flex -space-x-1.5" data-id="element-2471">
              <img src={TEAM_AVATARS.marcus} alt="" className="w-6 h-6 rounded-[5px] object-cover ring-2 ring-[#1A1D21]" data-id="element-2472" />
              <img src={TEAM_AVATARS.elena} alt="" className="w-6 h-6 rounded-[5px] object-cover ring-2 ring-[#1A1D21]" data-id="element-2473" />
              <img src={TEAM_AVATARS.priya} alt="" className="w-6 h-6 rounded-[5px] object-cover ring-2 ring-[#1A1D21]" data-id="element-2474" />
            </div>
            <MoreVertical size={16} className="text-white/45" data-id="element-2475" />
          </div>
        </div>

        {/* Body — Slack messages are flat rows, not chat bubbles */}
        <div className="flex-1 py-3 flex flex-col min-h-[268px]" data-id="element-2476">
          {/* User Message */}
          <div className="group flex gap-2.5 px-4 py-1.5 transition-colors hover:bg-white/[0.03]" data-id="element-2477">
            <img src={TEAM_AVATARS.marcus} alt="Maddie Cole" className="w-9 h-9 rounded-[6px] object-cover shrink-0" data-id="element-2478" />
            <div className="min-w-0" data-id="element-2479">
              <div className="flex items-baseline gap-2" data-id="element-2480">
                <span className="text-white font-bold text-[15px] leading-none" data-id="element-2481">
                  Maddie Cole
                </span>
                <span className="text-white/35 text-xs" data-id="element-2482">9:41 AM</span>
              </div>
              <p className="mt-1 text-white/85 text-[15px] leading-snug" data-id="element-2483">
                Hey{' '}
                <span className="rounded-[3px] bg-[#1D9BD1]/15 px-1 font-medium text-[#5CB8E8]" data-id="element-2484">
                  @Giga
                </span>{' '}
                pull this month's pipeline and flag anything slipping
              </p>
            </div>
          </div>

          {/* Giga Message */}
          <div className="group relative flex gap-2.5 px-4 py-1.5 transition-colors hover:bg-white/[0.03]" data-id="element-2485">
            <div className="w-9 h-9 rounded-[6px] bg-[#EA580C] shrink-0 flex items-center justify-center" data-id="element-2486">
              <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-2487" />
            </div>
            <div className="min-w-0 w-full" data-id="element-2488">
              <div className="flex items-baseline gap-1.5" data-id="element-2489">
                <span className="text-white font-bold text-[15px] leading-none" data-id="element-2490">
                  Giga
                </span>
                <span className="rounded-[3px] bg-white/15 px-1 py-px text-[9px] font-bold uppercase leading-none tracking-wide text-white/60" data-id="element-2491">
                  App
                </span>
                <span className="text-white/35 text-xs" data-id="element-2492">9:41 AM</span>
              </div>

              <div className="relative mt-1.5" data-id="element-2493">
                <AnimatePresence mode="wait" data-id="element-2494">
                  {phase < 2 ? <motion.div key="thinking" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.15
                }} className="flex items-center gap-2 text-white/45 text-[15px] h-6" data-id="element-2495">
                      {phase === 0 ? <span data-id="element-2496">is typing…</span> : <>
                          <span data-id="element-2497">Scanning HubSpot + Slack</span>
                          <span className="flex gap-1" data-id="element-2498">
                            {[0, 1, 2].map(d => <motion.span key={d} className="w-1.5 h-1.5 rounded-full bg-[#FB923C]" animate={{
                        opacity: [0.3, 1, 0.3]
                      }} transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        delay: d * 0.15
                      }} data-id="element-2499" />)}
                          </span>
                        </>}
                    </motion.div> : <motion.div key="answer" initial={{
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.25,
                  ease: 'easeOut'
                }} data-id="element-2500">
                      <p className="mb-2 text-[15px] leading-snug text-white/85" data-id="element-2501">
                        Here's where this month stands 👇
                      </p>
                      {/* Slack message attachment — left accent bar, like a bot card */}
                      <div className="rounded-[6px] border-l-[3px] border-[#EA580C] bg-white/[0.04] py-2.5 pl-3 pr-3" data-id="element-2502">
                        <div className="flex flex-col gap-2" data-id="element-2503">
                          {GIGA_METRICS.map((m, i) => <motion.div key={m.label} initial={{
                        opacity: 0,
                        x: -8
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        duration: 0.25,
                        delay: 0.1 + i * 0.12
                      }} className="flex items-baseline justify-between gap-3" data-id="element-2504">
                              <span className="text-[12px] text-white/45 shrink-0" data-id="element-2505">
                                {m.label}
                              </span>
                              <span className="text-right text-[13px] font-semibold leading-tight text-white" data-id="element-2506">
                                {m.value}
                                {m.delta && <span className="ml-1.5 text-[11px] font-medium text-[#FB923C]" data-id="element-2507">
                                    {m.delta}
                                  </span>}
                              </span>
                            </motion.div>)}
                        </div>
                      </div>
                      {/* Reactions row — makes it feel like a real Slack thread */}
                      <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    delay: 0.5,
                    duration: 0.2
                  }} className="mt-2 flex items-center gap-1.5" data-id="element-2508">
                        <span className="flex items-center gap-1 rounded-full border border-[#1D9BD1]/40 bg-[#1D9BD1]/15 px-2 py-0.5 text-xs font-semibold text-[#5CB8E8]" data-id="element-2509">
                          🔥 <span data-id="element-2510">3</span>
                        </span>
                        <span className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.06] px-2 py-0.5 text-xs font-semibold text-white/70" data-id="element-2511">
                          🙌 <span data-id="element-2512">2</span>
                        </span>
                        <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/40" data-id="element-2513">
                          <Smile size={13} data-id="element-2514" />
                        </span>
                      </motion.div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Footer — Slack composer */}
        <div className="px-4 pb-4 pt-1" data-id="element-2515">
          <div className="rounded-lg border border-white/15 bg-[#222529] overflow-hidden" data-id="element-2516">
            <div className="px-3 py-2 text-[15px] text-white/35" data-id="element-2517">
              Message #revenue-ops
            </div>
            <div className="flex items-center gap-3 border-t border-white/[0.07] px-3 py-1.5" data-id="element-2518">
              <Plus size={17} className="text-white/45" data-id="element-2519" />
              <Type size={17} className="text-white/45" data-id="element-2520" />
              <Smile size={17} className="text-white/45" data-id="element-2521" />
              <AtSign size={17} className="text-white/45" data-id="element-2522" />
              <div className="flex-1" data-id="element-2523" />
              <span className="flex h-6 w-6 items-center justify-center rounded bg-[#1D7D4D]" data-id="element-2524">
                <Send size={13} className="text-white" data-id="element-2525" />
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}
export function SlackIntegrationGraph() {
  const NUM_NODES = 180;
  const nodes = useMemo(() => {
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    return Array.from({
      length: NUM_NODES
    }).map((_, i) => {
      // Distribute nodes using a Vogel spiral
      const r = Math.sqrt(i + 0.5) / Math.sqrt(NUM_NODES) * 380;
      const theta = i * goldenAngle;
      // Add some random jitter
      const jitterR = (Math.random() - 0.5) * 30;
      const jitterTheta = (Math.random() - 0.5) * 0.3;
      const finalR = Math.max(90, r + jitterR); // Keep away from center
      const x = Math.cos(theta + jitterTheta) * finalR;
      const y = Math.sin(theta + jitterTheta) * finalR;
      const slug = SLUGS[Math.floor(Math.random() * SLUGS.length)];
      // Randomize node size slightly
      const size = 16 + Math.random() * 12;
      return {
        id: i,
        x,
        y,
        slug,
        size
      };
    });
  }, []);
  return <section className="relative w-full min-h-screen overflow-hidden flex items-center justify-center py-20 bg-[#2B211A]" data-id="element-2526">
      <div className="max-w-[1400px] w-full mx-auto px-6 md:px-12 flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-0" data-id="element-2527">
        {/* LEFT: Slack Panel */}
        <SlackPanel data-id="element-2528" />

        {/* RIGHT: Integration Graph */}
        <div className="relative w-full max-w-[800px] aspect-square flex items-center justify-center" data-id="element-2529">
          {/* SVG Lines */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{
          overflow: 'visible'
        }} data-id="element-2530">
            <defs data-id="element-2531">
              <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-2532">
                <stop offset="0%" stopColor="#EA580C" stopOpacity="0.8" data-id="element-2533" />
                <stop offset="100%" stopColor="#EA580C" stopOpacity="0.1" data-id="element-2534" />
              </linearGradient>
            </defs>

            {/* Horizontal connecting line from Slack to Center */}
            {/* We use a fixed percentage/coordinate approach for responsiveness,
             assuming the center of the graph is at 50%, 50% of this container.
             The line extends to the left out of the container to meet the Slack panel. */}
            <motion.line className="hidden lg:block" x1="-20%" y1="56%" x2="50%" y2="50%" stroke="#EA580C" strokeWidth="1.5" strokeDasharray="4 4" initial={{
            pathLength: 0,
            opacity: 0
          }} whileInView={{
            pathLength: 1,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeInOut'
          }} data-id="element-2535" />

            {/* Lines from center to nodes */}
            {nodes.map((node, i) => <motion.line key={`line-${i}`} x1="50%" y1="50%" x2={`calc(50% + ${node.x}px)`} y2={`calc(50% + ${node.y}px)`} stroke="#EA580C" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="3 3" initial={{
            pathLength: 0,
            opacity: 0
          }} whileInView={{
            pathLength: 1,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 1,
            delay: 0.5 + Math.random() * 1,
            ease: 'easeOut'
          }} data-id="element-2536" />)}
          </svg>

          {/* Center Giga Logo */}
          <motion.div className="absolute z-20 w-24 h-24 rounded-full bg-[#EA580C] flex items-center justify-center shadow-[0_0_40px_rgba(234,88,12,0.4)]" initial={{
          scale: 0,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3,
          type: 'spring'
        }} data-id="element-2537">
            <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-12 h-12 object-contain [filter:brightness(0)_invert(1)]" data-id="element-2538" />
          </motion.div>

          {/* Nodes */}
          {nodes.map((node, i) => <motion.div key={`node-${i}`} className="absolute z-10 rounded-full bg-white flex items-center justify-center shadow-lg" style={{
          width: node.size,
          height: node.size,
          left: `calc(50% + ${node.x}px)`,
          top: `calc(50% + ${node.y}px)`,
          x: '-50%',
          y: '-50%'
        }} initial={{
          scale: 0,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.8 + Math.random() * 1,
          type: 'spring'
        }} data-id="element-2539">
              <img src={`https://cdn.simpleicons.org/${node.slug}`} alt="" className="w-3/5 h-3/5 object-contain" onError={e => {
            ;
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }} data-id="element-2540" />
            </motion.div>)}
        </div>
      </div>
    </section>;
}